/* eslint-disable @typescript-eslint/no-use-before-define */
import { useTogglePresentationMode } from '@euler/app/components/PresentationModeContext';
import { useIsWeixinOpenAuthorized } from '@euler/app/flows/auth/context';
import { useLogout } from '@euler/app/flows/auth/hooks';
import { StatusDotLabel } from '@euler/app/flows/erp/components/StatusDotLabel';
import { useErpProviders } from '@euler/app/flows/erp/functions/useErpProviders';
import {
  AppSharingModal,
  AppSharingModalRef,
} from '@euler/app/flows/settings/components/AppSharingModal';
import { BluetoothGatewayDeviceCountNote } from '@euler/app/flows/settings/components/BluetoothGatewayDeviceCountNote';
import {
  InviteModal,
  InviteModalRef,
} from '@euler/app/flows/settings/components/InviteModal';
import { ObdDeviceCountNote } from '@euler/app/flows/settings/components/ObDeviceCountNote';
import { TableCellDetailLabel } from '@euler/app/flows/settings/components/TableCellDetailLabel';
import { VersionInfo } from '@euler/app/flows/settings/components/VersionInfo';
import { useOpenDashboardMiniprogram } from '@euler/app/flows/settings/functions/useOpenDashboardMiniprogram';
import { useWeixinCustomServiceChat } from '@euler/app/flows/settings/functions/useWeixinCustomServiceChat';
import { ErpIntegrationIcon } from '@euler/app/flows/settings/icons/ErpIntegrationIcon';
import { SettingsNavParams } from '@euler/app/flows/settings/screens';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { TableView } from '@euler/components/TableView';
import { FontFamily } from '@euler/components/typography';
import { useLocalCacheInfo } from '@euler/functions/cache/useLocalCacheInfo';
import { useStoreFeature } from '@euler/functions/entitlements/useStoreFeature';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { isMobileBrowser } from '@euler/functions/responsiveness';
import { useIsWeixinAvailable } from '@euler/functions/useIsWeixinAvailable';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import {
  useScreenContentContainerStyle,
  wrapNavigatorScreen,
} from '@euler/functions/wrapNavigatorScreen';
import { ErpIntegrationType, StoreFeature } from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore, showAlert, withConfirmation } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { filesize } from 'filesize';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Platform, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { Cell, Section } from '../components/SettingsTable';

const kIconColor = '#999';
const kIconSize = 24;

const NotSupportedNote = memo(() => {
  const { rpx } = useResponsiveContext();
  return (
    <Icon
      name="exclamation-circle-o"
      size={rpx(16)}
      color={StatusColors.Notice}
    />
  );
});

export const SettingsScreen = wrapNavigatorScreen(
  () => {
    const navigation = useNavigation<StackNavigationProp<SettingsNavParams>>();

    const { safeAreaInsets } = useSystemMetrics();
    const { rpx } = useResponsiveContext();
    const iconSize = rpx(kIconSize);
    const contentContainerStyle = useScreenContentContainerStyle();

    const isWeixinAvailable = useIsWeixinAvailable();
    const isWeixinOpenAuthorized = useIsWeixinOpenAuthorized();

    const [cacheSize, isLoadingCacheSize, clearCache] = useLocalCacheInfo();

    const onUnsupportedPress = useCallback(() => {
      void showAlert('提示', '很抱歉，该功能暂不支持当前设备类型！');
    }, []);

    const onObdDeviceCellPress = useCallback(() => {
      navigation.navigate('_obdDevices', {});
    }, [navigation]);

    const onGatewayDeviceCellPress = useCallback(() => {
      navigation.navigate('_gatewayDevices', {});
    }, [navigation]);

    const onProfilePress = useCallback(() => {
      navigation.navigate('_profile', {});
    }, [navigation]);

    const onAccountSecurityPress = useCallback(() => {
      navigation.navigate('_accountSecurity', {});
    }, [navigation]);

    const onGeneralSettingsPress = useCallback(() => {
      navigation.navigate('_generalSettings', {});
    }, [navigation]);

    const onNotificationSettingsPress = useCallback(() => {
      navigation.navigate('_notificationSettings', {});
    }, [navigation]);

    const { storeErpIntegrationService } = useServiceFactory();
    const { getProviderName } = useErpProviders();
    const isFocused = useIsFocused();
    const [erpProviderName, setErpProviderName] = useState<
      string | undefined
    >();
    const [hasActiveErpIntegration, setHasActiveErpIntegration] =
      useState(false);
    const [hasOpenapiErpIntegration, setHasOpenapiErpIntegration] =
      useState(false);

    const isErpIntegrationEnabled = useStoreFeature(
      StoreFeature.ErpIntegration,
    ).enabled;

    // The entitlement covers the two mechanisms a store sets up itself. An
    // openapi integration is provisioned by us and can exist without it, so
    // those stores still get the section — read-only, to see what is running.
    const showErpSection = isErpIntegrationEnabled || hasOpenapiErpIntegration;

    const onErpBindingPress = useCallback(() => {
      navigation.navigate('erpIntegration', {});
    }, [navigation]);

    // Reloaded on focus so the cell reflects a binding made or removed on the
    // ERP flow the cell itself navigates to.
    useEffect(() => {
      if (!isFocused) return;
      let disposed = false;
      const loadErpDetail = async () => {
        try {
          // Read the connection rather than the browser extension: a store
          // running only a hosted integration has no extension row, and
          // would otherwise read as unbound.
          const { connections } =
            await storeErpIntegrationService.getErpConnections();
          const live = connections.find(x => x.activeIntegrationCount > 0);
          if (!disposed) {
            setHasActiveErpIntegration(live != null);
            setHasOpenapiErpIntegration(
              connections.some(c =>
                c.activeIntegrations.some(
                  i => i.type === ErpIntegrationType.Openapi,
                ),
              ),
            );
            setErpProviderName(
              live ? getProviderName(live.erpProviderId, '') : undefined,
            );
          }
        } catch {
          if (!disposed) {
            setHasActiveErpIntegration(false);
            setHasOpenapiErpIntegration(false);
            setErpProviderName(undefined);
          }
        }
      };
      void loadErpDetail();
      return () => {
        disposed = true;
      };
    }, [getProviderName, isFocused, storeErpIntegrationService]);

    const onWeixinCustomerServiceChatPress = useWeixinCustomServiceChat();

    const inviteModalRef = useRef<InviteModalRef>(null);
    const appSharingModalRef = useRef<AppSharingModalRef>(null);

    const onOpenInviteModal = useCallback(async () => {
      if (!isWeixinOpenAuthorized) {
        await showAlert('提示', '该操作需要授权微信公众号!');
        return;
      }
      inviteModalRef.current?.show();
    }, [isWeixinOpenAuthorized]);

    const onShareAppPress = useCallback(() => {
      appSharingModalRef.current?.show();
    }, []);

    const onHelpPress = useCallback(() => {
      navigation.navigate('help', {});
    }, [navigation]);

    const onAboutUsPress = useCallback(() => {
      navigation.navigate('_aboutUs', {});
    }, [navigation]);

    const togglePresentationMode = useTogglePresentationMode();
    const onTogglePresentationMode = useCallback(async () => {
      if (
        await withConfirmation({
          title: '切换模式',
          message: '是否切换到演示模式? ',
        })
      ) {
        await togglePresentationMode();
      }
    }, [togglePresentationMode]);

    const logout = useLogout();
    const onLogoutPress = usePersistFn(async () => {
      if (
        await withConfirmation({
          title: '退出登录',
          message: '确定要退出登录吗?',
        })
      ) {
        logout().catch(onErrorIgnore);
      }
    });

    const onCacheSettingsPress = usePersistFn(async () => {
      if (
        await withConfirmation({ title: '确认', message: '是否清除全部缓存?' })
      ) {
        await clearCache();
      }
    });

    const { canOpenDashboardMiniprogram, openDashboardMiniprogram } =
      useOpenDashboardMiniprogram();

    const onOpenDashboardMiniprogram = useCallback(async () => {
      if (!isWeixinOpenAuthorized) {
        await showAlert('提示', '该操作需要授权微信公众号!');
        return;
      }
      void openDashboardMiniprogram();
    }, [isWeixinOpenAuthorized, openDashboardMiniprogram]);

    return (
      <View
        css={`
          flex: 1;
        `}
      >
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={[
            {
              paddingTop: rpx(15),
              paddingBottom: safeAreaInsets.bottom + rpx(10),
            },
            contentContainerStyle,
          ]}
        >
          <TableView>
            <Section title="通用设置">
              <Cell
                title="个人信息"
                icon={<Icon name="user-o" size={iconSize} color={kIconColor} />}
                detailIcon={'disclosure'}
                onPress={onProfilePress}
              />
              <Cell
                title="账号安全"
                icon={<Icon name="lock" size={iconSize} color={kIconColor} />}
                detailIcon={'disclosure'}
                onPress={onAccountSecurityPress}
              />
              <Cell
                title="通用设置"
                icon={
                  <Icon
                    name="app_settings_alt"
                    size={iconSize}
                    color={kIconColor}
                  />
                }
                detailIcon={'disclosure'}
                onPress={onGeneralSettingsPress}
              />
              <Cell
                title="通知设置"
                icon={
                  <Icon
                    name="notifications-outline"
                    size={iconSize}
                    color={kIconColor}
                  />
                }
                detailIcon={'disclosure'}
                onPress={onNotificationSettingsPress}
              />
              <Cell
                title="应用缓存"
                icon={<Icon name="layers" size={iconSize} color={kIconColor} />}
                detailIcon={'disclosure'}
                detail={
                  isLoadingCacheSize || cacheSize == null ? (
                    <ActivityIndicator
                      size="small"
                      css={`
                        transform: scale(0.75);
                      `}
                    />
                  ) : (
                    `${cacheSize ? filesize(cacheSize) : ''}`
                  )
                }
                onPress={onCacheSettingsPress}
              />
            </Section>
            {showErpSection && (
              <Section title="第三方服务">
                <Cell
                  title="服务集成与管理"
                  icon={
                    <ErpIntegrationIcon size={iconSize} color={kIconColor} />
                  }
                  detail={
                    hasActiveErpIntegration && erpProviderName ? (
                      <StatusDotLabel
                        tone="ok"
                        label={erpProviderName}
                        // Matches the plain detail text of its neighbours;
                        // only the dot is meant to carry the state.
                        labelStyle={{
                          color: '#666',
                          fontFamily: FontFamily.NotoSans.Thin,
                          fontSize: rpx(12),
                        }}
                      />
                    ) : (
                      erpProviderName
                    )
                  }
                  detailIcon={'disclosure'}
                  onPress={onErpBindingPress}
                />
              </Section>
            )}
            <Section title="其他">
              {canOpenDashboardMiniprogram && (
                <Cell
                  title="门店看板"
                  icon={
                    <Icon name="clipboard" size={iconSize} color={kIconColor} />
                  }
                  detail={'进入小程序门店看板'}
                  detailIcon={'disclosure'}
                  onPress={onOpenDashboardMiniprogram}
                />
              )}
              <Cell
                title="邀请同事"
                icon={
                  <Icon name="user-add" size={iconSize} color={kIconColor} />
                }
                detailIcon={'disclosure'}
                detail={
                  isWeixinOpenAuthorized ? undefined : '(未授权微信公众号)'
                }
                detailTextStyle={
                  isWeixinOpenAuthorized
                    ? undefined
                    : {
                        color: StatusColors.Danger,
                        fontFamily: FontFamily.NotoSans.Medium,
                      }
                }
                onPress={onOpenInviteModal}
              />
              <Cell
                title="分享应用"
                icon={<Icon name="share" size={iconSize} color={kIconColor} />}
                detailIcon={'disclosure'}
                onPress={onShareAppPress}
              />
              {isWeixinAvailable && (
                <Cell
                  title="微信客服"
                  icon={
                    <Icon
                      name="smile-comment"
                      size={iconSize + rpx(2)}
                      color={kIconColor}
                    />
                  }
                  detailIcon={'disclosure'}
                  onPress={onWeixinCustomerServiceChatPress}
                />
              )}
              <Cell
                title="使用说明"
                icon={
                  <Icon
                    name="help_outline"
                    size={iconSize}
                    color={kIconColor}
                    css={`
                      margin-left: ${rpx(2)}px;
                    `}
                  />
                }
                detailIcon={'disclosure'}
                onPress={onHelpPress}
              />
              {!isMobileBrowser() && (
                <Cell
                  title="切换模式"
                  icon={
                    <Icon
                      name="share-arrow"
                      size={iconSize}
                      color={kIconColor}
                      css={`
                        margin-left: ${rpx(2)}px;
                      `}
                    />
                  }
                  detailIcon={'disclosure'}
                  onPress={onTogglePresentationMode}
                />
              )}
              <Cell
                title="关于我们"
                icon={
                  <Icon
                    name="exclamation-circle-o"
                    size={iconSize - rpx(4)}
                    color={kIconColor}
                    css={`
                      margin-left: ${rpx(5)}px;
                    `}
                  />
                }
                detailIcon={'disclosure'}
                onPress={onAboutUsPress}
              />
            </Section>
            {Platform.OS !== 'web' && (
              <Section title="常用工具">
                <Cell
                  title="OBD诊断"
                  detailIcon={'disclosure'}
                  detail={<NotSupportedNote />}
                  icon={<Icon name="obd" size={iconSize} color={kIconColor} />}
                  onPress={onUnsupportedPress}
                />
                <Cell
                  title="保养复位"
                  detailIcon={'disclosure'}
                  detail={<NotSupportedNote />}
                  icon={
                    <Icon name="oil-time" size={iconSize} color={kIconColor} />
                  }
                  onPress={onUnsupportedPress}
                />
                <Cell
                  title="内窥镜"
                  detailIcon={'disclosure'}
                  detail={<NotSupportedNote />}
                  icon={
                    <Icon
                      name="endoscope"
                      size={iconSize + rpx(4)}
                      color={kIconColor}
                      css={`
                        width: ${iconSize}px;
                      `}
                    />
                  }
                  onPress={onUnsupportedPress}
                />
                <Cell
                  title="刹车复位"
                  detailIcon={'disclosure'}
                  detail={<NotSupportedNote />}
                  icon={
                    <Icon
                      name="restoration"
                      size={iconSize}
                      color={kIconColor}
                    />
                  }
                  onPress={onUnsupportedPress}
                />
                <Cell
                  title="节气门匹配"
                  detailIcon={'disclosure'}
                  icon={
                    <Icon name="throttler" size={iconSize} color={kIconColor} />
                  }
                  detail={<NotSupportedNote />}
                  onPress={onUnsupportedPress}
                />
              </Section>
            )}
            <Section title="设备管理">
              {Platform.OS !== 'web' && (
                <Cell
                  title="蓝牙网关"
                  icon={
                    <Icon
                      name="bluetooth-o"
                      size={iconSize}
                      color={kIconColor}
                    />
                  }
                  detailIcon={'disclosure'}
                  detail={
                    <TableCellDetailLabel>
                      <BluetoothGatewayDeviceCountNote />
                    </TableCellDetailLabel>
                  }
                  onPress={onGatewayDeviceCellPress}
                />
              )}
              <Cell
                title="OBD设备"
                icon={
                  <Icon name="devices" size={iconSize} color={kIconColor} />
                }
                detailIcon={'disclosure'}
                detail={
                  <TableCellDetailLabel>
                    <ObdDeviceCountNote />
                  </TableCellDetailLabel>
                }
                onPress={onObdDeviceCellPress}
              />
            </Section>
            <Section>
              <Cell
                title="退出登录"
                titleTextStyle={{ color: StatusColors.Danger }}
                detailIcon={'disclosure'}
                onPress={onLogoutPress}
              />
            </Section>
          </TableView>
          <VersionInfo />
        </ScrollView>
        <InviteModal ref={inviteModalRef} />
        <AppSharingModal ref={appSharingModalRef} />
      </View>
    );
  },
  {
    title: '设置',
  },
);
